/**
 * PHARMAKON — Secours (vendre avant l'achat) — MIROIR CLIENT de la config.
 *
 * PLAN_pharmacy_batch1_modifications §2 · commanditaire §13.
 *
 * Le serveur (`apps/server/src/config/secours.config.js`) reste L'AUTORITÉ :
 * il refuse un lot mal formé, une quantité au-delà du plafond, une officine
 * saturée. Ce fichier n'accorde rien — il évite un geste perdu au comptoir en
 * disant « non » avant l'aller-retour réseau, et il porte la copie française
 * normative (§8.5) pour qu'un écran et un message d'erreur ne divergent pas.
 *
 * Les plafonds sont RAFRAÎCHIS depuis `GET /secours` (`data.config`) dès que la
 * page en a l'occasion : les valeurs ci-dessous sont un défaut de démarrage, pas
 * une vérité. Si un exploitant change `SECOURS_PLAFOND_LIGNE` côté serveur, le
 * POS suit sans redéploiement.
 */

/** Défaut de démarrage — Q1 du plan. Remplacé par la valeur serveur. */
export const SECOURS_DEFAUTS = {
  plafond_ligne: 10,
  plafond_officine: 20,
  orphelin_apres_jours: 7,
};

/** Longueur minimale du n° de lot, une fois normalisé. */
export const SECOURS_LOT_MIN = 2;
/** Longueur maximale de la saisie brute. */
export const SECOURS_LOT_MAX = 30;

/**
 * MIROIR EXACT de la règle serveur : on retire tout ce qui n'est ni lettre ni
 * chiffre, et on met en majuscules. `AB-123`, `ab 123` et `AB.123` sont le même
 * lot ; `AB123` et `AB1234` restent deux lots distincts.
 */
export function normaliserNumeroLot(s) {
  return String(s ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/** Les seuls séparateurs qu'on lit vraiment sur une boîte. */
const CARACTERES = /^[A-Za-z0-9][A-Za-z0-9\-/. ]*$/;

/**
 * Contrôle de forme, identique au serveur. Rend un motif FRANÇAIS prêt à
 * afficher sous le champ — jamais un booléen nu, qui obligerait l'appelant à
 * réinventer le message.
 *
 * @param {string} brut
 * @returns {{ok: boolean, motif?: string, numero_lot?: string}}
 */
export function verifierNumeroLot(brut) {
  const t = String(brut ?? "").trim();
  if (!t) return { ok: false, motif: "Le N° de lot est obligatoire." };
  if (t.length > SECOURS_LOT_MAX) {
    return { ok: false, motif: `${SECOURS_LOT_MAX} caractères maximum.` };
  }
  if (!CARACTERES.test(t)) {
    return {
      ok: false,
      motif: "Lettres, chiffres, tiret, point et barre oblique uniquement.",
    };
  }
  if (normaliserNumeroLot(t).length < SECOURS_LOT_MIN) {
    return {
      ok: false,
      motif: `Au moins ${SECOURS_LOT_MIN} caractères alphanumériques.`,
    };
  }
  return { ok: true, numero_lot: t };
}

/** §8.5 — la copie normative, au vouvoiement. */
export const SECOURS_COPIE = {
  vendre: "Vendre comme secours",
  rupture: "Signaler une rupture",
  badge: "Secours",
  lot: "N° de lot",
  lotAide: "Relevez-le sur la boîte — il servira à rattacher l'achat.",
  drawerTitre: "Produits vendus en secours",
  drawerVide:
    "Aucune vente de secours en attente. Tout ce qui a été vendu en avance de phase a été rattaché à un achat.",
  confirmationTitre: "Des ventes de secours concernent cet achat",
  rattacher: "Rattacher",
  ignorer: "Ignorer et créer les lots complets",
  ruptureFaite: "Rupture signalée au responsable",
  ruptureDeja: "Rupture déjà signalée pour ce produit",
};

/**
 * La CLÉ d'identité d'une ligne de secours dans le panier du POS.
 *
 * Le panier identifie chaque ligne par `stock_lot_id` (quantité, changement de
 * lot, sélection clavier, suppression). Une ligne de secours n'a justement PAS
 * de lot en stock — d'où une clé synthétique, reconnaissable, posée dans le même
 * champ pour que TOUTE la mécanique existante du panier continue de marcher sans
 * modification. Elle est retirée à la construction du payload : le serveur reçoit
 * `stock_lot_id: null` et l'objet `secours`.
 */
export const SECOURS_LIGNE_PREFIXE = "SEC::";

export function cleLigneSecours(produit_id, numero_lot) {
  return `${SECOURS_LIGNE_PREFIXE}${produit_id}::${normaliserNumeroLot(numero_lot)}`;
}

export function estCleSecours(id) {
  return typeof id === "string" && id.startsWith(SECOURS_LIGNE_PREFIXE);
}

/**
 * Le `stock_lot_id` à ENVOYER au serveur pour une ligne de panier : la clé
 * synthétique d'une ligne de secours ne doit jamais franchir le réseau (le
 * validateur exige un UUID, et il a raison).
 */
export function stockLotIdPourEnvoi(ligne) {
  return estCleSecours(ligne?.stock_lot_id) ? null : (ligne?.stock_lot_id ?? null);
}

/**
 * Le fragment `secours` d'une ligne de panier, prêt à être étalé dans le détail
 * envoyé au serveur. Vide pour une ligne ordinaire — le chemin normal ne change
 * pas d'un octet.
 */
export function fragmentSecours(ligne) {
  if (!ligne?.secours) return {};
  return {
    secours: {
      numero_lot: ligne.secours.numero_lot,
      ...(ligne.secours.date_peremption_declaree
        ? { date_peremption_declaree: ligne.secours.date_peremption_declaree }
        : {}),
      // X XWP-37 — le prix pratiqué au comptoir, confirmé par le caissier : c'est lui que le serveur
      // facture (SAISI_SECOURS) et inscrit sur la dette de lot ; obligatoire sur toute ligne de secours.
      ...(ligne.secours.prix_confirme != null ? { prix_confirme: ligne.secours.prix_confirme } : {}),
    },
  };
}

// ─── X XWP-37 (XD-214, XD-240) — le prix pratiqué au comptoir ────────────────

/**
 * MIROIR de `apps/server/src/config/secours.config.js` SOURCE_PRIX_SECOURS : d'où vient le prix
 * proposé par « Vendre comme secours » (`GET /produits/:id/prix-secours`), dans cet ordre.
 */
export const SOURCE_PRIX_SECOURS = Object.freeze({
  DERNIER_ACHAT: "DERNIER_ACHAT",
  GENERIQUE: "GENERIQUE",
  PRODUIT: "PRODUIT",
});

export const SECOURS_PRIX_MESSAGES = Object.freeze({
  libelle: "Prix pratiqué (DA)",
  requis: "Saisissez le prix pratiqué",
  aucun: "Aucun prix connu : saisissez le prix pratiqué au comptoir",
  chargement: "Recherche du dernier prix…",
});

/** Deux décimales au plus, une virgule ou un point — la forme que le serveur lit (`montantTexte`). */
const FORME_PRIX = /^\d{1,15}(?:[.,]\d{1,2})?$/;

/**
 * Le prix saisi, lu COMME LE SERVEUR le lit : chaîne décimale > 0, deux décimales au plus ; rendu
 * canonique « 260.00 » prêt pour `secours.prix_confirme`. ≤ 0, vide ou illisible → refus nommé.
 * @param {string|number|null|undefined} brut
 * @returns {{ ok: true, prix: string } | { ok: false, motif: string }}
 */
export function verifierPrixSecours(brut) {
  const t = String(brut ?? "").trim();
  if (!FORME_PRIX.test(t)) return { ok: false, motif: SECOURS_PRIX_MESSAGES.requis };
  const [ent, dec = ""] = t.replace(",", ".").split(".");
  const centimes = Number(ent) * 100 + Number(dec.padEnd(2, "0"));
  if (!(centimes > 0)) return { ok: false, motif: SECOURS_PRIX_MESSAGES.requis };
  return { ok: true, prix: `${Math.floor(centimes / 100)}.${String(centimes % 100).padStart(2, "0")}` };
}

/**
 * Le libellé de la source du prix proposé, lu par la personne au comptoir sous le champ « Prix ».
 * @param {{ source: string, produit_reference?: { nom_commercial: string }|null }|null} suggestion
 */
export function libelleSourcePrixSecours(suggestion) {
  switch (suggestion?.source) {
    case SOURCE_PRIX_SECOURS.DERNIER_ACHAT:
      return "Prix de votre dernier achat de ce produit";
    case SOURCE_PRIX_SECOURS.GENERIQUE:
      return `Prix du dernier achat d'un générique (même DCI, dosage et forme) : ${suggestion.produit_reference?.nom_commercial ?? "—"}`;
    case SOURCE_PRIX_SECOURS.PRODUIT:
      return "PPA du produit";
    default:
      return SECOURS_PRIX_MESSAGES.aucun;
  }
}
